import React, { useMemo, useState, useRef } from 'react';
import HeaderBar from 'components/main/Setup/CrystalContainer/HeaderBar';
import RMTable from 'components/customComponents/Controls/RMTable';
import RMCheckBox from 'components/customComponents/Controls/tableSelectionCheckBox';
import { SaveContainerDTO } from 'business/models/Models';
import { createNewContainerDto } from 'components/main/Setup/PlateTypes/utils';
import {
  PlateTypeEnum,
  PlateTypeOptions,
  PlateTypesInfo,
} from 'components/main/Setup/PlateTypes/types';
import { EISelectBox } from 'components/customComponents/Layers/EICheckBox';
import RMModal, { RMModalProps } from 'components/modal/RMModal';
import { IStyle } from 'Styles';
import TableSortInputFilter from 'components/customComponents/Controls/tableSortInputFilter';
import { useModalHandlers } from 'components/modal';
import { FmlxBanner } from 'fmlx-common-ui';
import {
  plateTypeEditButtons,
  plateTypeEditHeading,
  plateTypeInImagerEditMessage,
  plateTypeInUseEditMessage,
  plateTypeInUseEditWarningMessage,
} from 'components/main/Setup/PlateTypes/constants';

interface PlateDefinitionProps {
  currentPlateTypes: SaveContainerDTO[];
  setCurrentPlateTypes: React.Dispatch<React.SetStateAction<SaveContainerDTO[]>>;
  selectedIds: number[];
  setSelectedIds: (number) => void;
  selectedIndices: number[];
  setSelectedIndices: (number) => void;
  deletedPlateTypeIds: number[];
  setDeletedPlateTypeIds: React.Dispatch<React.SetStateAction<number[]>>;
  setIsDirty: React.Dispatch<React.SetStateAction<boolean>>;
  deletedDropIds: number[];
  setDeletedDropIds: React.Dispatch<React.SetStateAction<number[]>>;
  plateTypeErrors: PlateTypesInfo;
  editId: number;
  setEditId: React.Dispatch<React.SetStateAction<number>>;
  previousDropNums: number[];
  setPreviousDropNums: React.Dispatch<React.SetStateAction<number[]>>;
  handleChangeInDropNum: (number) => void;
  setIsNumDropsBlurToggle: React.Dispatch<React.SetStateAction<boolean>>;
  enableDropsByDefault: boolean;
}

const defaultEditModeClasses = 'bg-white p-2 w-full';
const defaultEditModeStyle = { border: '1px solid black', borderRadius: '0.25rem' };
const defaultEditMode = {
  className: defaultEditModeClasses,
  style: defaultEditModeStyle,
};
const defaultNumberFieldProps = {
  nonEditMode: { className: 'max-w-[75px]' },
  editMode: defaultEditMode,
};
const cellProperties = {
  name: {
    nonEditMode: { className: 'min-w-[115px] text-center' },
    editMode: defaultEditMode,
  },
  numRows: {
    nonEditMode: { className: 'max-w-[75px]' },
    editMode: defaultEditMode,
  },
  numColumns: {
    nonEditMode: { className: 'max-w-[75px]' },
    editMode: defaultEditMode,
  },
  maxWellVolume: defaultNumberFieldProps,
  defaultWellVolume: defaultNumberFieldProps,
  defaultAdditiveVolume: defaultNumberFieldProps,
  maxNumDrops: {
    nonEditMode: { className: 'max-w-[75px]' },
    editMode: defaultEditMode,
  },
  maxDropVolume: defaultNumberFieldProps,
  defaultDropVolume: defaultNumberFieldProps,
  defaultSeedVolume: defaultNumberFieldProps,
  defaultFragmentVolume: defaultNumberFieldProps,
  type: {},
  enable: {},
};

const defaultEnabledColumns = [
  'name',
  'numRows',
  'numColumns',
  'maxWellVolume',
  'defaultWellVolume',
  'defaultAdditiveVolume',
  'maxNumDrops',
  'maxDropVolume',
  'defaultDropVolume',
  'defaultSeedVolume',
  'defaultFragmentVolume',
  'type',
  'enable',
];
const handleTableFilter = (filterTitle: string) => (event: any) =>
  <TableSortInputFilter event={event} title={filterTitle} showSearchInput={false} />;
function PlateDefinition(props: PlateDefinitionProps) {
  const {
    currentPlateTypes,
    setCurrentPlateTypes,
    selectedIds,
    setSelectedIds,
    selectedIndices,
    setSelectedIndices,
    deletedPlateTypeIds,
    setDeletedPlateTypeIds,
    setIsDirty,
    plateTypeErrors,
    editId,
    setEditId,
    setPreviousDropNums,
    handleChangeInDropNum,
    setIsNumDropsBlurToggle,
    enableDropsByDefault,
  } = props;

  const inputRef = useRef<HTMLInputElement | null>(null);
  const [modal, setModal] = useState<RMModalProps>();
  const [showModal, setShowModal] = useState(false);
  const { showModal: showInUseModal, hideModal: hideInUseModal } = useModalHandlers();

  const getPlateTypeInUseEditMessageBody = () => {
    return (
      <>
        <FmlxBanner
          data-testid="plate-definition-in-user-warning-edit-banner"
          variant="warning"
          subtitle={plateTypeInUseEditWarningMessage}
          hideCloseButton
        />
        <div data-testid="plate-definition-in-use-warning-edit-message" className="my-2">
          {plateTypeInUseEditMessage}
        </div>
      </>
    );
  };

  const onEdit = () => {
    const plateType = currentPlateTypes.find((x) => x.id === selectedIds?.[0]);
    if (plateType?.inUse) {
      showInUseModal({
        heading: plateTypeEditHeading,
        body: getPlateTypeInUseEditMessageBody(),
        okButtonText: plateTypeEditButtons.Continue,
        onOk: () => {
          hideInUseModal();
          setEditId(selectedIds[0]);
          setTimeout(() => {
            if (inputRef?.current) {
              inputRef.current.focus();
            }
          }, 200);
        },
        onCancel: hideInUseModal,
      });
      return;
    }
    setEditId(selectedIds[0]);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleChangeInDropNum(selectedIndices[0]);
      e.preventDefault();
      setEditId(null);
    }
  };

  const onBlur = () => {
    handleChangeInDropNum(selectedIndices[0]);
    setIsNumDropsBlurToggle((prev) => {
      return !prev;
    });
  };

  const changePlateType = (e) => {
    const val = Number(e.target.value);
    setCurrentPlateTypes((prev) => {
      const updatedPrev = [...prev];
      const updatedItem = { ...updatedPrev[selectedIndices[0]] };
      const plateTypeAttributesToReset = [
        'defaultAdditiveVolume',
        'maxNumDrops',
        'defaultDropVolume',
        'maxDropVolume',
        'defaultSeedVolume',
        'defaultFragmentVolume',
      ];
      updatedItem['type'] = val;
      if (updatedItem['type'] === PlateTypeEnum['Screen Lot Plate']) {
        plateTypeAttributesToReset.forEach((key) => {
          updatedItem[key] = 0;
        });
      }
      updatedPrev[selectedIndices[0]] = new SaveContainerDTO(updatedItem);
      return updatedPrev;
    });
    setIsDirty(true);
  };
  const replaceSelectedWithUpdatedItem = (e, idx) => {
    e.stopPropagation();
    const nonNumberColumns = ['name', 'enable'];
    const { name, type, value } = e.target;

    if (!nonNumberColumns.includes(name)) {
      //For all columns that have number values
      if (isNaN(value)) {
        return;
      }
    }

    setCurrentPlateTypes((prev) => {
      const updatedPrev = [...prev];
      const updatedItem = { ...updatedPrev[idx] };
      if (type === 'checkbox' && name === 'enable') {
        if (updatedItem.isAtImager && updatedItem[name]) {
          showInUseModal({
            heading: plateTypeEditHeading,
            body: plateTypeInImagerEditMessage,
            okButtonText: plateTypeEditButtons.Ok,
            isOkOnly: true,
            onOk: hideInUseModal,
          });
        } else {
          updatedItem[name] = !updatedItem[name];
        }
      } else {
        updatedItem[name] = value;
      }
      updatedPrev[idx] = new SaveContainerDTO(updatedItem);
      return updatedPrev;
    });
    setIsDirty(true);
  };

  const generateSortType =
    (key: string, selectedValueKey = 'data-selectedValue') =>
    (a, b) => {
      if (!a?.original?.[key]?.props || !b) {
        return 0;
      }
      return (
        a.original[key].props[selectedValueKey] - b.original[key].props[selectedValueKey]
      );
    };

  const columns = useMemo(
    () => [
      {
        Header: 'Name',
        accessor: 'name',
        className: 'justify-center max-w-[150px] min-w-[150px]',
        editOptions: { inputType: 'text' },
        Filter: handleTableFilter('Name'),
        sortType: generateSortType('name'),
      },
      {
        Header: 'Rows',
        accessor: 'numRows',
        className: 'justify-center max-w-[60px] min-w-[60px]',
        editOptions: { inputType: 'text' },
        Filter: handleTableFilter('Rows'),
        sortType: generateSortType('numRows'),
      },
      {
        Header: 'Cols',
        accessor: 'numColumns',
        className: 'justify-center max-w-[60px] min-w-[60px]',
        editOptions: { inputType: 'text' },
        Filter: handleTableFilter('Cols'),
        sortType: generateSortType('numColumns'),
      },
      {
        Header: 'Max Well Vol.(uL)',
        accessor: 'maxWellVolume',
        className: 'justify-center max-w-[132px] min-w-[132px]',
        editOptions: { inputType: 'text' },
        Filter: handleTableFilter('Max Well Vol.(uL)'),
      },
      {
        Header: 'Def. Well Vol.(uL)',
        accessor: 'defaultWellVolume',
        className: 'justify-center max-w-[134px] min-w-[134px]',
        editOptions: { inputType: 'text' },
        Filter: handleTableFilter('Def. Well Vol.(uL)'),
      },
      {
        Header: 'Def. Additive Vol.(uL)',
        accessor: 'defaultAdditiveVolume',
        editOptions: { inputType: 'text' },
        className: 'justify-center max-w-[150px] min-w-[150px]',
        Filter: handleTableFilter('Def. Additive Vol.(uL)'),
      },
      {
        Header: 'Drops',
        accessor: 'maxNumDrops',
        className: 'justify-center max-w-[65px] min-w-[65px]',
        editOptions: { inputType: 'text' },
        Filter: handleTableFilter('Drops'),
        sortType: generateSortType('maxNumDrops'),
      },
      {
        Header: 'Max Drop Vol.(uL)',
        accessor: 'maxDropVolume',
        className: 'justify-center max-w-[150px] min-w-[150px]',
        editOptions: { inputType: 'text' },
        Filter: handleTableFilter('Max Drop Vol.(uL)'),
      },
      {
        Header: 'Def. Drop Vol.(uL)',
        accessor: 'defaultDropVolume',
        className: 'justify-center max-w-[150px] min-w-[150px]',
        editOptions: { inputType: 'text' },
        Filter: handleTableFilter('Def. Drop Vol.(uL)'),
      },
      {
        Header: 'Def. Seed Vol.(uL)',
        accessor: 'defaultSeedVolume',
        className: 'justify-center max-w-[150px] min-w-[150px]',
        editOptions: { inputType: 'text' },
        Filter: handleTableFilter('Def. Seed Vol.(uL)'),
      },
      {
        Header: 'Def. Fragment Vol.(uL)',
        accessor: 'defaultFragmentVolume',
        className: 'justify-center max-w-[150px] min-w-[150px]',
        editOptions: { inputType: 'text' },
        Filter: handleTableFilter('Def. Fragment Vol.(uL)'),
      },
      {
        Header: 'Type',
        accessor: 'type',
        className: 'justify-center max-w-[150px] min-w-[150px]',
        editOptions: { inputType: 'text' },
        Filter: handleTableFilter('Type'),
        sortType: generateSortType('type', 'selectedValue'),
      },
      {
        Header: 'Enabled',
        accessor: 'enable',
        className: 'justify-center',
        editOptions: { inputType: 'text' },
      },
    ],
    [],
  );

  const tableRows = () => {
    return currentPlateTypes.map((plateType: SaveContainerDTO, idx) => {
      let plateTypeToRender = {};
      Object.keys(cellProperties).forEach((cellKey) => {
        if (cellKey === 'type') {
          plateTypeToRender['type'] = (
            <EISelectBox
              data-testid="plate-definition-select-type"
              labelText=""
              selectedValue={plateType.type}
              onChange={changePlateType}
              options={PlateTypeOptions}
              disabled={plateType.inUse}
              containerStyle={styles.typeCell}
            />
          );
        } else if (cellKey === 'enable') {
          plateTypeToRender['enable'] = (
            <RMCheckBox
              data-testid="plate-definition-enable-check"
              id={`platedefinition-tablerows-${idx}-enable-checkbox`}
              name="enable"
              onChange={(e) => {
                replaceSelectedWithUpdatedItem(e, idx);
              }}
              checked={plateType.enable}
            />
          );
        } else if (!(editId === plateType.id)) {
          plateTypeToRender[cellKey] = cellProperties[cellKey].nonEditMode ? (
            <div
              id={`platedefinition-tablerows-${idx}-${cellKey}-nonedit`}
              className={cellProperties[cellKey].nonEditMode.className}
              data-selectedValue={plateType[cellKey]}
            >
              {plateType[cellKey]}
            </div>
          ) : (
            plateType[cellKey]
          );
        } else {
          let enabledColumns: string[];
          if (plateType.inUse) {
            enabledColumns = [
              'name',
              'maxWellVolume',
              'defaultWellVolume',
              'defaultAdditiveVolume',
              'maxNumDrops',
              'maxDropVolume',
              'defaultDropVolume',
              'defaultSeedVolume',
              'defaultFragmentVolume',
            ];
          } else if (plateType.type === PlateTypeEnum['Screen Lot Plate']) {
            enabledColumns = [
              'name',
              'numRows',
              'numColumns',
              'maxWellVolume',
              'defaultWellVolume',
              'enable',
            ];
          } else {
            enabledColumns = defaultEnabledColumns;
          }

          const isDisableKey = !enabledColumns.includes(cellKey);
          plateTypeToRender[cellKey] = (
            <input
              id={`platedefinition-tablerows-${idx}-${cellKey}-input`}
              type="text"
              key={plateType.id}
              name={cellKey}
              value={plateType[cellKey]}
              className={`${cellProperties[cellKey].editMode.className} ${
                isDisableKey ? 'opacity-60' : ''
              }`}
              style={cellProperties[cellKey].editMode.style}
              onChange={(e) => {
                replaceSelectedWithUpdatedItem(e, idx);
              }}
              disabled={isDisableKey}
              onKeyDown={handleKeyPress}
              {...(cellKey === 'name' ? { ref: inputRef } : {})}
              {...(cellKey === 'maxNumDrops' ? { onBlur: onBlur } : {})}
            />
          );
        }
      });
      plateTypeToRender = {
        ...plateType,
        ...plateTypeToRender,
      };
      return plateTypeToRender;
    });
  };

  const handleOnRowCLick = (row) => {
    if (selectedIndices.length > 0) {
      handleChangeInDropNum(selectedIndices[0]);
    }
    if (selectedIds.length > 0 && row.index === selectedIndices[0]) {
      return;
    }
    const newIndices = [];
    newIndices.push(row.index);
    setSelectedIndices(newIndices);
    setSelectedIds([row.original.id]);
    setEditId(null);
  };
  const onAddPlateType = () => {
    const newContainerDto = createNewContainerDto(enableDropsByDefault);
    setCurrentPlateTypes([...currentPlateTypes, newContainerDto]);
    setEditId(newContainerDto.id);
    setSelectedIds([newContainerDto.id]);
    setSelectedIndices([currentPlateTypes.length]);
    setIsDirty(true);
    setPreviousDropNums((prev) => {
      const updatedPrev = [...prev, newContainerDto.maxNumDrops];
      return updatedPrev;
    });
  };
  const onDeletePlateType = () => {
    if (currentPlateTypes[selectedIndices[0]].inUse) {
      setModal((prev) => ({
        ...prev,
        body: 'You cannot delete this container because it is currently in use.',
        heading: `Delete`,
        okButtonText: 'OK',
        isOkOnly: true,
      }));
      setShowModal(true);
    } else {
      if (selectedIds[0] > 0) {
        setDeletedPlateTypeIds([...deletedPlateTypeIds, selectedIds[0]]);
      }
      setCurrentPlateTypes((prev) => {
        const filteredData = prev.filter(
          (currentPlateType) => currentPlateType.id !== selectedIds[0],
        );
        return filteredData;
      });
      setSelectedIds([]);
      setSelectedIndices([]);
      setIsDirty(true);
      setPreviousDropNums((prev) => {
        const updatedPrev = prev.filter((id) => id !== selectedIds[0]);
        return updatedPrev;
      });
    }
  };

  return (
    <div id="platedefinition-wrapper" style={styles.plateDefinitionContainer}>
      <div id="platedefinition-content">
        <HeaderBar
          data-testid="plate-definition-header-bar"
          title="Plate Definition"
          onAdd={onAddPlateType}
          onDelete={onDeletePlateType}
          onEdit={onEdit}
          enableEditDelete={selectedIndices.length > 0}
        />
        <RMTable
          data-testid="plate-definition-rm-table"
          columns={columns}
          data={tableRows()}
          tableClass={'w-100'}
          selectedIds={selectedIndices}
          onRowClicked={handleOnRowCLick}
          rowExtraInfo={plateTypeErrors}
          mainContainerStyle={styles.plateDefinitionTable}
        />
      </div>
      <RMModal
        data-testid="plate-definition-rm-modal"
        id="platedefinition-modal"
        showModal={showModal}
        setShowModal={setShowModal}
        onOk={() => setShowModal(false)}
        {...modal}
      />
    </div>
  );
}

const styles: IStyle = {
  plateDefinitionTable: {
    height: '72vh',
    overflow: 'scroll',
  },
  typeCell: {
    minWidth: '150px',
  },
  plateDefinitionContainer: {
    width: '91%',
  },
};
export default PlateDefinition;
